import {
  type AccountInfo,
  type IPublicClientApplication
} from "@azure/msal-browser";
import { authLogger } from "./auth-logger";

// MSAL Debug Utility to avoid circular dependencies
export const msalDebugUtils = {
  checkForMultipleInstances: (
    instances: IPublicClientApplication[] = []
  ): void => {
    if (instances.length > 1) {
      authLogger.warn(`Multiple MSAL instances detected: ${instances.length}`, {
        instanceCount: instances.length
      });
    }
  },

  logTokenInfo: (account: AccountInfo): void => {
    authLogger.debug("Account Information", {
      account: account.username,
      hasClaims: !!account.idTokenClaims,
      hasLocalAccountId: !!account.localAccountId,
      environment: account.environment
    });

    if (account.idTokenClaims) {
      authLogger.debug("ID Token Claims", {
        availableClaims: Object.keys(account.idTokenClaims),
        hasRoles: "roles" in account.idTokenClaims
      });
    }
  },

  logRoleExtraction: (
    originalRoles: string[],
    extractedRoles: string[]
  ): void => {
    authLogger.roleExtraction("Role extraction debug", {
      originalRoles,
      extractedRoles,
      missingRoles: originalRoles.filter(
        (role) => !extractedRoles.includes(role)
      )
    });
  },

  logMsalEvent: (eventType: string, payload?: unknown): void => {
    authLogger.debug(`MSAL Event: ${eventType}`, {
      eventType,
      hasPayload: !!payload
    });
  },

  logTokenAcquisition: (scopes: string[], account?: AccountInfo): void => {
    authLogger.debug("Token Acquisition", {
      scopes,
      account: account?.username
    });
  },

  logTokenValidation: (isValid: boolean, errors?: string[]): void => {
    authLogger.tokenValidation("Token validation result", {
      isValid,
      errorCount: errors?.length || 0
    });
  },

  logAuthenticationFlow: (flow: string, details?: unknown): void => {
    authLogger.debug(`Authentication Flow: ${flow}`, {
      flow,
      hasDetails: !!details
    });
  },

  logError: (error: unknown, context: string): void => {
    authLogger.authError(error, context);
  },

  getCacheStatus: () => {
    const sessionKeys = Object.keys(sessionStorage).filter(
      (key) => key.includes("msal") || key.includes("tempii")
    );
    const localKeys = Object.keys(localStorage).filter(
      (key) => key.includes("msal") || key.includes("tempii")
    );

    authLogger.cache("MSAL Cache Status", {
      sessionStorageCount: sessionKeys.length,
      localStorageCount: localKeys.length
    });

    return { sessionStorage: sessionKeys, localStorage: localKeys };
  }
};
